import * as React from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/system/Box";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import useRoutes from "src/hooks/useRoutes";
import useCourse from "src/hooks/useCourse";
import useResolvers from "src/hooks/useResolvers";
import useBulkQuestions from "src/hooks/useBulkQuestions";

import Loader from "src/components/Loader";
import Heading from "src/components/Heading";
import Playlist from "src/components/Course/Playlist";
import AssignemntStepper from "src/components/Stepper";
import AITagsSelection from "src/components/AITagsSelection";
import LecturePlayer from "src/components/Course/LecturePlayer";
import BulkQuestionForm from "src/components/Questions/BulkQuestionForm";

// Language config for the bulk question generation
export const languageConfig = {
  en: {
    showContextSelectionArea: true,
    showInstructionsInput: false,
    callDifferentApi: false,
  },
  tr: {
    showContextSelectionArea: false,
    showInstructionsInput: true,
    callDifferentApi: true,
  },
};

const AddNewQuestion = () => {
  const { t, i18n } = useTranslation();
  const { getState } = useRoutes();
  const { categories } = useCourse();
  const { LectureTypes } = useResolvers();
  const {
    loading,
    subject,
    lectures,
    onNext,
    onCancel,
    setSubject,
    activeStep,
    questionData,
    setActiveStep,
    selectedLecture,
    topicLectures,
    setQuestionData,
    setTopicLectures,
    addQuestionTags,
    // onSubmitQuestions,
    removeQuestionTag,
    setSelectedLecture,
    // resetQuestionData,
    onGenerateQuestions,
  } = useBulkQuestions();

  const state = getState();

  // Resetting page data
  // React.useEffect(() => () => resetQuestionData(), []);

  const ASSIGNMENT_STEPS = React.useMemo(
    () => [
      {
        title: t("questions.bulkQuestionGeneration.steps.step1Title"),
        description: t(
          "questions.bulkQuestionGeneration.steps.step1Description"
        ),
        disabled: !languageConfig[i18n?.language]?.showContextSelectionArea,
      },
      {
        title: t("questions.bulkQuestionGeneration.steps.step2Title"),
        description: t(
          "questions.bulkQuestionGeneration.steps.step2Description"
        ),
        disabled: !languageConfig[i18n?.language]?.showContextSelectionArea,
      },
    ],
    [t, i18n?.language]
  );

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const subject = state?.subject;
    if (subject) {
      setSubject(subject);
      // Setting selected course by default
      setQuestionData((prev) => ({ ...prev, courseId: subject?.courseId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  React.useEffect(() => {
    if (questionData?.keywords?.length) {
      const children = getChildrenIds(
        categories[subject?.id]?.categories,
        questionData?.keywords?.[0]
      );
      children.push(questionData?.keywords?.[0]);

      const lecs = lectures
        ?.filter((l) => l?.tags?.some((lt) => children?.includes(lt.id)))
        ?.filter((l) => l?.type === LectureTypes[t("lectureTypes.theory")]);

      if (lecs?.length) {
        setTopicLectures(lecs);
        setSelectedLecture(lecs?.[0]);
      }
    }
  }, [subject, lectures, questionData?.keywords, categories, t]);

  const getChildrenIds = (nodes, parentId) => {
    const childIds = [];

    function findChildren(nodeId) {
      nodes?.forEach((node) => {
        if (node?.parent === nodeId) {
          // Add child ID to the result list
          childIds.push(node?.id);
          // Recursively find children of this child
          findChildren(node?.id);
        }
      });
    }

    findChildren(parentId);

    return childIds;
  };

  const handleContextChange = (e, idx) => {
    setQuestionData((prev) => {
      const updatedContexts = [...(prev?.contexts || [])];

      updatedContexts[idx] = e?.target?.value || "";

      while (
        updatedContexts?.length > 1 &&
        updatedContexts[updatedContexts.length - 1].trim() === ""
      ) {
        updatedContexts.pop();
      }

      if (updatedContexts[updatedContexts.length - 1].trim() !== "") {
        updatedContexts.push("");
      }

      return { ...prev, contexts: updatedContexts };
    });
  };

  return (
    <React.Fragment>
      <Box className="p24" sx={{ height: "86.4%", position: "relative" }}>
        <Box className="filter-top-wrap align-center-corner">
          <Heading
            title={t("questions.bulkCreationGPT")}
            routes={[
              `${subject?.courseName ? subject?.courseName : subject?.name}`,
              t("questions.bulkCreationGPT"),
            ]}
          />
        </Box>

        <Grid
          container
          spacing={1}
          className="new-assignment-wrap add-question-wrap"
        >
          <AssignemntStepper
            {...{ steps: ASSIGNMENT_STEPS, activeStep, setActiveStep }}
          />
          {activeStep === 0 && (
            <>
              {/* Left Side */}
              <Grid item xs={7}>
                <BulkQuestionForm
                  questionData={questionData}
                  setQuestionData={setQuestionData}
                />
              </Grid>

              {/* Right Side */}
              <Grid item xs={5}>
                <AITagsSelection
                  required={true}
                  addTags={addQuestionTags}
                  courseId={subject?.courseId}
                  handleDelete={removeQuestionTag}
                  selectedTags={questionData?.keywords}
                />
              </Grid>
            </>
          )}
          {activeStep === 1 && (
            <>
              <Alert severity="info">
                {t("questions.bulkQuestionGeneration.steps.step1.info")}
              </Alert>

              <Grid item xs={3}>
                {/* Lectures list */}
                <Box className="play-list-wrapper selecters">
                  <Typography variant="body1" fontWeight={"bold"}>
                    {t(
                      "questions.bulkQuestionGeneration.steps.step1.topicBasedLectures"
                    )}
                  </Typography>
                  <Playlist
                    lectures={topicLectures}
                    selected={selectedLecture}
                    setSelected={setSelectedLecture}
                  />
                </Box>
              </Grid>

              {/* Lecture preview */}
              <Grid item xs={6}>
                <LecturePlayer
                  classname="selecters"
                  lectureUrl={selectedLecture?.link ?? ""}
                />
              </Grid>

              {/* Selected texts */}
              <Grid item xs={3} className="selecters">
                <Typography variant="body1" fontWeight={"bold"}>
                  {t(
                    "questions.bulkQuestionGeneration.steps.step1.selectedContexts"
                  )}
                </Typography>
                {questionData?.contexts?.map((text, idx) => (
                  <Box my={1} key={idx} className="custom-input-wrap bg-light">
                    <Typography variant="caption">{idx + 1}. </Typography>
                    <TextField
                      multiline
                      minRows={2}
                      maxRows={5}
                      size="small"
                      value={text}
                      variant="outlined"
                      onChange={(e) => handleContextChange(e, idx)}
                      inputProps={{ minLength: 50, maxLength: 3000 }}
                    />
                  </Box>
                ))}
              </Grid>
            </>
          )}
        </Grid>
        {loading ? (
          <Box className="align-center fullscreen-gpt-loader">
            <Loader />
          </Box>
        ) : null}
      </Box>

      {/* Footer */}
      <Box className="general-btm-blk">
        <>
          <Button
            disabled={loading}
            className="btn-secondary"
            onClick={activeStep === 0 ? onCancel : () => setActiveStep(0)}
          >
            {activeStep === 0 ? t("buttons.cancel") : t("buttons.back")}
          </Button>
          <Button
            disabled={loading}
            className="btn-primary ml-20"
            onClick={
              activeStep === 0
                ? languageConfig[i18n?.language]?.showContextSelectionArea
                  ? onNext
                  : onGenerateQuestions
                : onGenerateQuestions
            }
          >
            {activeStep === 0
              ? languageConfig[i18n?.language]?.showContextSelectionArea
                ? t("buttons.next")
                : t("buttons.generate")
              : questionData?.contexts?.length === 0 ||
                (questionData?.contexts?.length === 1 &&
                  questionData?.contexts?.[0] === "")
              ? t("buttons.skipAndgenerate")
              : t("buttons.generate")}
          </Button>
        </>

        {/* {activeStep === 0 ? (
          <>
            <Button
              className="btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              {t("buttons.cancel")}
            </Button>
            <Button
              onClick={onNext}
              disabled={loading}
              className="btn-primary ml-20"
            >
              {t("buttons.next")}
            </Button>
          </>
        ) : (
          <>
            <Button
              disabled={loading}
              className="btn-secondary"
              onClick={() => setActiveStep(0)}
            >
              {t("buttons.back")}
            </Button>
            <Button
              disabled={loading}
              className="btn-primary ml-20"
              onClick={onGenerateQuestions}
            >
              {!questionData?.contexts?.some((ctx) => ctx.trim() !== "")
                ? t("buttons.skipAndgenerate")
                : t("buttons.generate")}
            </Button>
          </>
        )} */}
      </Box>
    </React.Fragment>
  );
};

export default AddNewQuestion;
